import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import type { IQuery } from "@citrine/client";
import ClientBase from "@citrine/client";
import { queryClient } from "@citrine/client/utils";
import type { MajorStatus } from "@citrine/types/status";

import type { AuditInfo, StatusDetail } from "../common";
import type { TableData, TrainingData } from "../data-source";
import { getDataIdentifier } from "../data-source";
import type { IDescriptor } from "../descriptor";
import type { PredictorEvaluationExecution } from "../predictor-evaluation-execution";
import { PredictorEvaluationExecutionClient } from "../predictor-evaluation-execution";
import type { PredictorEvaluator } from "../predictor-evaluation-workflow";
import { PredictorSummaryClient } from "../predictor-summary";

import type {
  IGraphMLPredictorConfig,
  IPredictorConfig,
  PredictorEntity,
} from "./predictor";
import { transformPredictorConfig } from "./utils";

export interface AsyncDefaultPredictorResponse {
  data: DefaultPredictorResponse | null;
  id: string;
  metadata: {
    data_source_id: string;
    created: AuditInfo;
    updated: AuditInfo;
    status: MajorStatus;
    status_detail: StatusDetail[];
  };
}

export interface DefaultPredictorOutput {
  predictor_name: string;
  outputs: IDescriptor[];
}

export interface DefaultPredictorResponse {
  instance: IGraphMLPredictorConfig<TableData>;
  name: string;
  description: string;
  outputs?: DefaultPredictorOutput[];
}

type PredictorUpdate =
  | { updatable: false; update: null }
  | { updatable: true; update: IPredictorConfig };

export type PredictorSortOption = "create_time" | "update_time" | "name";
export interface PredictorQuery extends IQuery {
  page?: number;
  per_page?: number;
  name?: string;
  archived?: boolean;
  dataSource?: string;
  erds?: boolean;
  branch?: string;
  sort_by?: PredictorSortOption | `${PredictorSortOption}.asc`;
}

export type PredictorDesignSpaceCompatibilityStatus =
  | "COMPATIBLE"
  | "DISJOINT"
  | "OVERLAPPING"
  | "UNKNOWN";

export interface PredictorDesignSpaceCompatibility {
  design_space_id: string;
  status: PredictorDesignSpaceCompatibilityStatus;
  descriptor_diff: {
    design_space_complement: IDescriptor[];
    overlapping_descriptors: IDescriptor[];
    predictor_complement: IDescriptor[];
    superset_descriptors: IDescriptor[];
  };
  ingredients_and_labels_diff: {
    ingredients_diff: {
      predictor_complement: [string, string][];
      design_space_complement: [string, string][];
      overlapping_ingredients: [string, string][];
    };
    labels_diff: {
      predictor_complement: [string, string][];
      design_space_complement: [string, string][];
      overlapping_labels: [string, string][];
    };
  };
}

export class PredictorClient extends ClientBase<
  PredictorEntity,
  string,
  PredictorQuery
> {
  protected constructor(protected projectId: string) {
    super({
      endpoint: `/api/v3/projects/${projectId}/predictors`,
      findField: "response",
      defaultQuery: {
        page: 1,
        per_page: 20,
        name: undefined,
        archived: undefined,
        sort_by: undefined,
        dataSource: undefined,
        erds: undefined,
        branch: undefined,
      },
    });
  }

  static getInstance(projectId: string) {
    return new PredictorClient(projectId);
  }

  async getDefaultPredictorConfig(dataSource: TrainingData) {
    const defaultResponse = await this.request<
      AsyncDefaultPredictorResponse["data"]
    >({
      method: "POST",
      url: `${this.endpoint}/default`,
      data: { data_source: dataSource, pattern: "INFER" },
    });
    return {
      id: "",
      data: defaultResponse,
      metadata: {
        status: "SUCCEEDED",
      },
    };
  }

  useGetDefaultPredictorConfig(
    dataSource?: TrainingData,
    options?: Omit<
      UseQueryOptions<AsyncDefaultPredictorResponse>,
      "queryKey" | "queryFn"
    >,
  ): UseQueryResult<AsyncDefaultPredictorResponse> {
    return useQuery({
      ...options,
      queryKey: [
        "default-async-predictor",
        dataSource ? getDataIdentifier(dataSource) : "undefined",
      ],
      queryFn: () => this.getDefaultPredictorConfig(dataSource!),
      enabled: Boolean(dataSource) && options?.enabled !== false,
    });
  }

  createAsyncDefaultPredictorConfig(dataSource: TrainingData) {
    return this.request<AsyncDefaultPredictorResponse>({
      method: "POST",
      url: `${this.endpoint}/default-async`,
      data: { data_source: dataSource, pattern: "INFER" },
    });
  }

  getAsyncDefaultPredictorConfig(cacheId: string) {
    return this.request<AsyncDefaultPredictorResponse>({
      method: "GET",
      url: `${this.endpoint}/default-async/${cacheId}`,
    });
  }

  useGetAsyncDefaultPredictorConfig(
    cacheId?: string,
    options?: Omit<
      UseQueryOptions<AsyncDefaultPredictorResponse>,
      "queryKey" | "queryFn"
    >,
  ) {
    return useQuery({
      ...options,
      queryKey: ["default-async-predictor", cacheId ?? "undefined"],
      queryFn: () => this.getAsyncDefaultPredictorConfig(cacheId!),
      enabled: Boolean(cacheId) && options?.enabled !== false,
    });
  }

  useCreateAsyncDefaultPredictorConfig(
    dataSource?: TrainingData,
    options?: Omit<
      UseQueryOptions<AsyncDefaultPredictorResponse, AxiosError>,
      "queryKey" | "queryFn"
    >,
  ) {
    return useQuery({
      ...options,
      queryKey: [
        "create-async-default-predictor",
        dataSource ? getDataIdentifier(dataSource) : "undefined",
      ],
      queryFn: () => this.createAsyncDefaultPredictorConfig(dataSource!),
      enabled: Boolean(dataSource) && options?.enabled !== false,
    });
  }

  async findPredictorEvaluationExecution(
    predictor_id: string,
    version?: string,
  ): Promise<PredictorEvaluationExecution | null> {
    const executions = await PredictorEvaluationExecutionClient.getInstance(
      this.projectId,
    ).find({
      per_page: 1,
      predictor_id,
      predictor_version: version,
    });

    return executions[0] ?? null;
  }

  async getCorrelatedOutputs(id: string) {
    const { correlated_outputs } = await this.request<{
      correlated_outputs: string[][];
    }>({
      url: `${this.endpoint}/${id}/correlated-outputs`,
    });
    // ignore single outputs
    return correlated_outputs.filter((outputs) => outputs.length > 1);
  }

  useCorrelatedOutputs(
    id?: string,
    options: Omit<UseQueryOptions<string[][]>, "queryKey" | "queryFn"> = {},
  ) {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(id), "correlated-outputs"],
      queryFn: () => this.getCorrelatedOutputs(id!),
      enabled: Boolean(id && (options.enabled ?? true)),
    });
  }

  async trainAndEvaluate(
    id: string,
    evaluators: PredictorEvaluator[],
    createVersion?: boolean,
  ) {
    const result = await this.request<PredictorEntity>({
      method: "PUT",
      url: `${this.endpoint}/${id}/train-and-evaluate`,
      params: {
        create_version: createVersion,
      },
      data: evaluators,
    });
    this.invalidateAfterMutation(id);
    queryClient.setQueryData(this.getModelCacheKey(id), result);

    return result;
  }

  useTrainAndEvaluate(
    options: Omit<
      UseMutationOptions<
        PredictorEntity,
        AxiosError,
        {
          id: string;
          evaluators: PredictorEvaluator[];
          createVersion?: boolean;
        }
      >,
      "mutationFn"
    > = {},
  ): UseMutationResult<
    PredictorEntity,
    AxiosError,
    {
      id: string;
      evaluators: PredictorEvaluator[];
      createVersion?: boolean;
    }
  > {
    return useMutation({
      ...options,
      mutationFn: ({ id, evaluators, createVersion }) =>
        this.trainAndEvaluate(id, evaluators, createVersion),
    });
  }

  async train(id: string) {
    const result = await this.request<PredictorEntity>({
      method: "PUT",
      url: `${this.endpoint}/${id}/train`,
    });
    this.invalidateAfterMutation(id);
    queryClient.setQueryData(this.getModelCacheKey(id), result);
    return result;
  }

  useTrain(
    options: Omit<
      UseMutationOptions<PredictorEntity, AxiosError, string>,
      "mutationFn"
    > = {},
  ) {
    return useMutation({ ...options, mutationFn: (id) => this.train(id) });
  }

  async archive(id: string) {
    const result = await this.request<PredictorEntity>({
      method: "PUT",
      url: `${this.endpoint}/${id}/archive`,
    });

    this.invalidateAfterMutation(result.id);
    this.addModelToCache(result);
    PredictorSummaryClient.getInstance(this.projectId).invalidateAfterMutation(
      result.id,
    );

    return result;
  }

  useArchive(
    options: Omit<
      UseMutationOptions<PredictorEntity, AxiosError, string>,
      "mutationFn"
    > = {},
  ) {
    return useMutation({ ...options, mutationFn: (id) => this.archive(id) });
  }

  async restore(id: string) {
    const result = await this.request<PredictorEntity>({
      method: "PUT",
      url: `${this.endpoint}/${id}/restore`,
    });

    this.invalidateAfterMutation(result.id);
    this.addModelToCache(result);
    PredictorSummaryClient.getInstance(this.projectId).invalidateAfterMutation(
      result.id,
    );

    return result;
  }

  useRestore(
    options: Omit<
      UseMutationOptions<PredictorEntity, AxiosError, string>,
      "mutationFn"
    > = {},
  ) {
    return useMutation({ ...options, mutationFn: (id) => this.restore(id) });
  }

  async checkForUpdate(predictorId: string) {
    const data = await this.request<PredictorUpdate>({
      method: "GET",
      url: `${this.endpoint}/${predictorId}/update-check`,
    });
    return data;
  }

  useCheckForUpdate(
    id?: string,
    options: Omit<
      UseQueryOptions<PredictorUpdate>,
      "queryKey" | "queryFn"
    > = {},
  ) {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(id), "update-check"],
      queryFn: () => this.checkForUpdate(id!),
      enabled: Boolean(id && (options.enabled ?? true)),
    });
  }

  /** Uses the latest predictor version, for specific versions use the PredictorVersionClient variant */
  getDesignSpaceCompatibility(predictorId: string, designSpaceIds: string[]) {
    return this.request<PredictorDesignSpaceCompatibility[]>({
      method: "POST",
      url: `${this.endpoint}/${predictorId}/validate-design-space-compatibility`,
      data: {
        design_spaces: designSpaceIds,
      },
    });
  }

  /** Uses the latest predictor version, for specific versions use the PredictorVersionClient variant */
  useGetDesignSpaceCompatibility(
    predictorId?: string,
    designSpaceIds?: string[],
    options: Omit<
      UseQueryOptions<PredictorDesignSpaceCompatibility[]>,
      "queryKey" | "queryFn"
    > = {},
  ) {
    return useQuery({
      ...options,
      queryKey: [
        ...this.getModelCacheKey(predictorId),
        "design-space-compatibility",
        [...(designSpaceIds ?? [])].sort(),
      ],
      queryFn: () =>
        this.getDesignSpaceCompatibility(predictorId!, designSpaceIds!),
      enabled: Boolean(
        predictorId && designSpaceIds?.length && (options.enabled ?? true),
      ),
    });
  }

  invalidateAfterMutation(id?: string) {
    super.invalidateAfterMutation(id);
    /**
     * if the following is merely invalidated, a modal momentarily pops up when
     * editing the predictor while the background refetch occurs. this happens
     * because the modal was removed from the DOM by the time the data is saved,
     * so invalidation does not trigger an immediate refetch. instead, refetch
     * does not occur until the next visit.
     *
     * we're forcefully removing the query so that its cached state does not
     * cause the display of the stale modal, even for a split second.
     */
    queryClient.removeQueries({
      queryKey: [...this.getModelCacheKey(id), "check-for-update"],
    });
    queryClient.removeQueries({
      queryKey: [...this.getModelCacheKey(id), "report"],
    });
  }

  normalizeQuery({
    name,
    archived,
    dataSource,
    erds,
    branch,
    ...query
  }: PredictorQuery = {}): IQuery {
    const filters = [];
    if (name) {
      filters.push(`name eq ${name}`);
    }

    if (archived !== undefined) {
      filters.push(`archived eq ${archived}`);
    }

    if (dataSource) {
      filters.push(`data_source eq ${dataSource}`);
    }

    if (branch !== undefined) {
      filters.push(`for_branch eq ${branch}`);
    }

    if (erds !== undefined) {
      filters.push(`include_erds eq ${erds}`);
    }

    return {
      ...super.normalizeQuery(query),
      filter: filters.length ? filters.join(",") : undefined,
    };
  }

  protected toModel(payload: any): PredictorEntity {
    payload.data.instance.type ||= "Graph";
    const model = super.toModel(payload);
    transformPredictorConfig(model.data.instance);
    return model;
  }

  protected fromModel(model: Partial<PredictorEntity>) {
    // create and update only take the config, not the entire entity object
    return { instance: model.data?.instance };
  }
}
